import React from 'react';
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  useTheme,
} from '@mui/material';
import {
  ExpandMore,
  CheckCircle,
  Cancel,
  Star,
  TrendingUp,
  TrendingDown,
  Info,
} from '@mui/icons-material';
import { getAllBehavioralTraits } from '../utils/behavioralDescriptions';

interface DetailedMatchBreakdownProps {
  candidate: {
    first_name: string;
    last_name: string;
    position_match_score: number;
    pi_factor_a: number;
    pi_factor_b: number;
    pi_factor_c: number;
    pi_factor_d: number;
  };
  matchBreakdown?: any;
  job?: any;
}

const DetailedMatchBreakdown: React.FC<DetailedMatchBreakdownProps> = ({
  candidate,
  matchBreakdown,
  job,
}) => {
  const theme = useTheme();

  // Calculate overall match percentage
  const matchPercentage = candidate.position_match_score 
    ? Math.round(candidate.position_match_score * 10) 
    : 0;

  // Get match color based on percentage
  const getMatchColor = (percentage: number) => {
    if (percentage >= 85) return theme.palette.success.main;
    if (percentage >= 70) return theme.palette.warning.main;
    if (percentage >= 55) return theme.palette.warning.dark;
    return theme.palette.error.main;
  };

  // Get recommendation based on score
  const getRecommendation = (percentage: number) => {
    if (percentage >= 85) return { text: 'Excellent match - Strong recommendation to proceed', icon: Star, color: 'success' };
    if (percentage >= 70) return { text: 'Good match - Recommended candidate', icon: TrendingUp, color: 'success' };
    if (percentage >= 55) return { text: 'Moderate match - Consider with additional evaluation', icon: Info, color: 'warning' };
    if (percentage >= 30) return { text: 'Weak match - Proceed with caution', icon: TrendingDown, color: 'warning' };
    return { text: 'Poor match - Not recommended for this role', icon: Cancel, color: 'error' };
  };

  const recommendation = getRecommendation(matchPercentage);

  // Factor scoring breakdown
  const getFactorBreakdown = () => {
    if (!matchBreakdown || !job) return [];

    const factors = [
      { key: 'factor_a', label: 'A - Dominance', candidateValue: candidate.pi_factor_a, ideal: [job.ideal_factor_a_min, job.ideal_factor_a_max] },
      { key: 'factor_b', label: 'B - Extraversion', candidateValue: candidate.pi_factor_b, ideal: [job.ideal_factor_b_min, job.ideal_factor_b_max] },
      { key: 'factor_c', label: 'C - Patience', candidateValue: candidate.pi_factor_c, ideal: [job.ideal_factor_c_min, job.ideal_factor_c_max] },
      { key: 'factor_d', label: 'D - Formality', candidateValue: candidate.pi_factor_d, ideal: [job.ideal_factor_d_min, job.ideal_factor_d_max] },
    ];

    return factors.map(factor => {
      const score = matchBreakdown.factor_scores?.[factor.key] || 0;
      const [min, max] = factor.ideal;
      const status = score === 1 ? 'perfect' : score === 0.5 ? 'close' : 'poor';
      const deviation = factor.candidateValue < min 
        ? min - factor.candidateValue 
        : factor.candidateValue > max 
          ? factor.candidateValue - max 
          : 0;

      return {
        ...factor,
        score,
        status,
        deviation,
        explanation: getFactorExplanation(factor.label, status, factor.candidateValue, [min, max]),
      };
    });
  };

  const getFactorExplanation = (factorName: string, status: string, candidateValue: number, ideal: [number, number]) => {
    // Convert to sigma scale for cleaner display
    const scaleToSigma = (value: number) => (value - 50) / 16.67;
    const roundToQuarter = (sigma: number) => Math.round(sigma * 4) / 4;
    const candidateSigma = scaleToSigma(candidateValue);
    const idealMinSigma = roundToQuarter(scaleToSigma(ideal[0]));
    const idealMaxSigma = roundToQuarter(scaleToSigma(ideal[1]));
    
    if (status === 'perfect') {
      return `${factorName} score of ${candidateSigma.toFixed(2)}σ falls perfectly within the ideal range (${idealMinSigma}σ to ${idealMaxSigma}σ).`;
    }
    
    if (status === 'close') {
      if (candidateValue < ideal[0]) {
        return `${factorName} score of ${candidateSigma.toFixed(2)}σ is slightly below the ideal range (${idealMinSigma}σ to ${idealMaxSigma}σ), but close enough to be acceptable.`;
      } else {
        return `${factorName} score of ${candidateSigma.toFixed(2)}σ is slightly above the ideal range (${idealMinSigma}σ to ${idealMaxSigma}σ), but close enough to be acceptable.`;
      }
    }
    
    if (candidateValue < ideal[0]) {
      return `${factorName} score of ${candidateSigma.toFixed(2)}σ is significantly below the ideal range (${idealMinSigma}σ to ${idealMaxSigma}σ). This may indicate challenges in this behavioral area.`;
    } else {
      return `${factorName} score of ${candidateSigma.toFixed(2)}σ is significantly above the ideal range (${idealMinSigma}σ to ${idealMaxSigma}σ). This may indicate potential overemphasis in this behavioral area.`;
    }
  };

  // Get behavioral traits for the candidate
  const candidateBehavioralTraits = React.useMemo(() => {
    if (!candidate.pi_factor_a || !candidate.pi_factor_b || !candidate.pi_factor_c || !candidate.pi_factor_d) {
      return null;
    }
    return getAllBehavioralTraits(
      candidate.pi_factor_a,
      candidate.pi_factor_b,
      candidate.pi_factor_c,
      candidate.pi_factor_d
    );
  }, [candidate]);

  // Enhanced relationship scoring breakdown with behavioral descriptions
  const getRelationshipBreakdown = () => {
    if (!matchBreakdown || !job || !candidateBehavioralTraits) return [];

    const relationships = [
      { req: 'requires_a_greater_than_b', score: 'a_greater_than_b', factors: ['A', 'B'], label: 'A > B', traitKey: 'A:B' },
      { req: 'requires_a_greater_than_c', score: 'a_greater_than_c', factors: ['A', 'C'], label: 'A > C', traitKey: 'A:C' },
      { req: 'requires_a_greater_than_d', score: 'a_greater_than_d', factors: ['A', 'D'], label: 'A > D', traitKey: 'A:D' },
      { req: 'requires_b_greater_than_a', score: 'b_greater_than_a', factors: ['B', 'A'], label: 'B > A', traitKey: 'A:B' },
      { req: 'requires_b_greater_than_c', score: 'b_greater_than_c', factors: ['B', 'C'], label: 'B > C', traitKey: 'B:C' },
      { req: 'requires_b_greater_than_d', score: 'b_greater_than_d', factors: ['B', 'D'], label: 'B > D', traitKey: 'B:D' },
      { req: 'requires_c_greater_than_a', score: 'c_greater_than_a', factors: ['C', 'A'], label: 'C > A', traitKey: 'A:C' },
      { req: 'requires_c_greater_than_b', score: 'c_greater_than_b', factors: ['C', 'B'], label: 'C > B', traitKey: 'B:C' },
      { req: 'requires_c_greater_than_d', score: 'c_greater_than_d', factors: ['C', 'D'], label: 'C > D', traitKey: 'C:D' },
      { req: 'requires_d_greater_than_a', score: 'd_greater_than_a', factors: ['D', 'A'], label: 'D > A', traitKey: 'A:D' },
      { req: 'requires_d_greater_than_b', score: 'd_greater_than_b', factors: ['D', 'B'], label: 'D > B', traitKey: 'B:D' },
      { req: 'requires_d_greater_than_c', score: 'd_greater_than_c', factors: ['D', 'C'], label: 'D > C', traitKey: 'C:D' },
    ];

    return relationships
      .filter(rel => job[rel.req])
      .map(rel => {
        const score = matchBreakdown.comparison_scores?.[rel.score] || 0;
        const met = score === 1;
        const behavioralEval = candidateBehavioralTraits[rel.traitKey];
        
        // Determine what trait the job requires
        const jobRequires = rel.label.includes('>') ? 
          (rel.label.split(' > ')[0] === rel.traitKey.split(':')[0] ? 'greater' : 'lesser') :
          'greater';
        
        const alignsWithRequirement = 
          (jobRequires === 'greater' && behavioralEval.candidateHas === 'greater') ||
          (jobRequires === 'lesser' && behavioralEval.candidateHas === 'lesser');

        return {
          ...rel,
          score,
          met,
          behavioralEval,
          jobRequires,
          alignsWithRequirement,
          traitName: behavioralEval.trait.name,
        };
      });
  };


  const factorBreakdown = getFactorBreakdown();
  const relationshipBreakdown = getRelationshipBreakdown();

  return (
    <Stack spacing={3}>
      {/* Overall Score Summary */}
      <Paper sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <recommendation.icon sx={{ color: getMatchColor(matchPercentage), fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold" color={getMatchColor(matchPercentage)}>
              {matchPercentage}% Match
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Overall Position Compatibility
            </Typography>
          </Box>
        </Box>

        <LinearProgress
          variant="determinate"
          value={matchPercentage}
          sx={{
            height: 12,
            borderRadius: 6,
            backgroundColor: 'action.disabled',
            mb: 2,
            '& .MuiLinearProgress-bar': {
              backgroundColor: getMatchColor(matchPercentage),
              borderRadius: 6,
            },
          }}
        />

        <Chip
          label={recommendation.text}
          color={recommendation.color as any}
          sx={{
            fontWeight: 'medium',
            '& .MuiChip-label': { px: 2 },
          }}
        />

        {/* Score Components Summary */}
        {matchBreakdown && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2,
              mt: 2,
            }}
          >
            <Box textAlign="center">
              <Typography variant="h4" color="primary.main">
                {matchBreakdown.factor_total?.toFixed(1) || 0}/4
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Factor Alignment Score
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="h4" color="primary.main">
                {matchBreakdown.comparison_total?.toFixed(1) || 0}/{(matchBreakdown.possible_total || 4) - 4}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Relationship Requirements Score
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Factor Scoring Detail */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Factor Alignment Analysis</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            How well the candidate's behavioral factors align with the position requirements
          </Typography>
          
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Factor</strong></TableCell>
                  <TableCell><strong>Candidate</strong></TableCell>
                  <TableCell><strong>Ideal Range</strong></TableCell>
                  <TableCell><strong>Score</strong></TableCell>
                  <TableCell><strong>Assessment</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {factorBreakdown.map((factor, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{factor.label}</TableCell>
                    <TableCell>{((factor.candidateValue - 50) / 16.67).toFixed(2)}σ</TableCell>
                    <TableCell>{Math.round(((factor.ideal[0] - 50) / 16.67) * 4) / 4}σ to {Math.round(((factor.ideal[1] - 50) / 16.67) * 4) / 4}σ</TableCell>
                    <TableCell>
                      <Chip
                        label={`${factor.score}/1.0`}
                        size="small"
                        color={factor.status === 'perfect' ? 'success' : factor.status === 'close' ? 'warning' : 'error'}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography variant="body2">{factor.explanation}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Relationship Requirements Analysis */}
      {relationshipBreakdown.length > 0 && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">Relationship Requirements Analysis</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Specific behavioral relationships required for optimal role performance
            </Typography>
            
            <Stack spacing={2}>
              {relationshipBreakdown.map((rel, idx) => {
                const trait = rel.behavioralEval.trait;
                const candidateLabel = rel.behavioralEval.label;
                const candidateStrength = rel.behavioralEval.strength;
                const jobRequiresLabel = rel.jobRequires === 'greater' ? trait.greaterLabel : trait.lesserLabel;
                
                return (
                  <Paper 
                    key={idx} 
                    variant="outlined" 
                    sx={{ 
                      p: 2,
                      borderColor: rel.met ? 'success.light' : 'error.light',
                      backgroundColor: rel.met ? 'success.light' : 'error.light',
                      bgcolor: rel.met ? 'rgba(76, 175, 80, 0.04)' : 'rgba(244, 67, 54, 0.04)',
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                      <Typography variant="h6" component="span">
                        {trait.name}
                      </Typography>
                      <Chip
                        icon={rel.met ? <CheckCircle /> : <Cancel />}
                        label={rel.met ? 'Met' : 'Not Met'}
                        size="small"
                        color={rel.met ? 'success' : 'error'}
                      />
                      <Typography variant="body2" color="text.secondary">
                        ({rel.label})
                      </Typography>
                    </Box>
                    
                    <Box display="flex" gap={4} flexWrap="wrap" sx={{ mb: 1 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Job Requires:</strong>
                        </Typography>
                        <Typography variant="body1">
                          {jobRequiresLabel}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Candidate Demonstrates:</strong>
                        </Typography>
                        <Typography variant="body1">
                          {candidateLabel} {candidateStrength !== 'balanced' && `(${candidateStrength})`}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Behavioral Description:</strong>
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      {rel.behavioralEval.detailedDescription.split('. ').filter(s => s.trim()).map((sentence, sentIdx) => (
                        <Typography 
                          key={sentIdx} 
                          variant="body2" 
                          sx={{ 
                            fontStyle: 'italic',
                            mb: 0.5,
                            '&:before': {
                              content: '"• "',
                              color: 'text.secondary',
                            }
                          }}
                        >
                          {sentence.trim()}{!sentence.endsWith('.') && '.'}
                        </Typography>
                      ))}
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Scoring Methodology */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Scoring Methodology</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            <strong>Factor Alignment Scoring:</strong>
          </Typography>
          <ul>
            <li><Typography variant="body2">1.0 points: Factor falls within ideal range</Typography></li>
            <li><Typography variant="body2">0.5 points: Factor is close to ideal range (within 25% tolerance)</Typography></li>
            <li><Typography variant="body2">0.0 points: Factor is outside acceptable range</Typography></li>
          </ul>
          
          <Typography variant="body2" paragraph sx={{ mt: 2 }}>
            <strong>Relationship Requirements Scoring:</strong>
          </Typography>
          <ul>
            <li><Typography variant="body2">1.0 points: Required relationship is met (e.g., A &gt; B when required)</Typography></li>
            <li><Typography variant="body2">0.0 points: Required relationship is not met</Typography></li>
          </ul>
          
          <Typography variant="body2" paragraph sx={{ mt: 2 }}>
            <strong>Total Score Calculation:</strong>
          </Typography>
          <Typography variant="body2">
            (Factor Score + Relationship Score) / (4 + Number of Active Relationships) × 10 = Percentage Match
          </Typography>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

export default DetailedMatchBreakdown;